import React, { useCallback, useContext, useMemo, useRef } from "react";
import { StyleSheet, View } from "react-native";
import { useTheme } from "react-native-elements";
import { getConcatenatedRequirementsForCraft } from "../../backend/events/campus/LevelUp";
import {
  calculateInventoryCap,
  getInventory,
  getTotalAmountOfItem,
} from "../../backend/items/Inventory";
import { Item } from "../../backend/items/Item";
import { PlayerContext } from "../../backend/PlayerContext";
import { Button } from "../utility/Button";
import { CostViewer } from "../utility/CostViewer";
import { Markdown } from "../utility/Markdown";
import { Text } from "../utility/Text";
import { ItemInfo } from "./ItemInfo";

export interface CraftingDetailsProps {
  item: Item;
}

export function CraftingInfo(props: CraftingDetailsProps) {
  const playerContext = useContext(PlayerContext);

  const item = props.item;
  const params = item.generateCraftedParams(playerContext);
  const totalOwnedText = `Total owned: ${getTotalAmountOfItem(
    item,
    playerContext,
  )}`;
  const cost = item.getCraftingMaterials(playerContext);
  const isCraftable = item.isCraftable(playerContext);

  const theme = useTheme();

  const concatenatedRequirements = getConcatenatedRequirementsForCraft(
    playerContext,
    item,
  );
  const cannotCraftText = `You do not meet the requirements to craft this item. You need: ${concatenatedRequirements}`;

  return useMemo(
    () => (
      <View>
        <ItemInfo
          itemStack={{
            itemOccurrence: { itemId: item.getId(), params },
            amount: 1,
          }}
          showTile={false}
        />
        <Markdown>{totalOwnedText}</Markdown>
        <View style={styles.costContainer}>
          <View style={styles.costTextContainer}>
            <Markdown>Cost:</Markdown>
          </View>
          <CostViewer resources={cost.resources} items={cost.items} />
        </View>
        {!isCraftable && (
          <View style={styles.actionRow}>
            <Text
              style={[
                styles.cannotCraftText,
                { color: theme.theme.colors.warning },
              ]}
            >
              {cannotCraftText}
            </Text>
          </View>
        )}
      </View>
    ),
    [
      item.getId(),
      JSON.stringify(params),
      totalOwnedText,
      JSON.stringify(cost),
      isCraftable,
      cannotCraftText,
      theme,
    ],
  );
}

export function CraftingDetails(props: CraftingDetailsProps) {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;

  const item = props.item;
  const params = item.generateCraftedParams(playerContext);
  const totalOwnedText = `Total owned: ${getTotalAmountOfItem(
    item,
    playerContext,
  )}`;

  const cost = item.getCraftingMaterials(playerContext);
  const canCraft = item.canCraft(playerContext);
  const isCraftable = item.isCraftable(playerContext);
  const hasCraftingMaterials = item.hasCraftingMaterials(playerContext);
  const inventoryFull =
    getInventory(playerContext).length === calculateInventoryCap(playerContext);
  const theme = useTheme();

  const concatenatedRequirements = getConcatenatedRequirementsForCraft(
    playerContext,
    item,
  );
  const cannotCraftText = `You do not meet the requirements to craft this item. You need: ${concatenatedRequirements}`;

  const craftCallback = useCallback(() => {
    playerContextRef.current.apply(item.craft.bind(item));
  }, [item]);

  return useMemo(
    () => (
      <View style={styles.craftingDetailsContainer}>
        <ItemInfo
          itemStack={{
            itemOccurrence: { itemId: item.getId(), params },
            amount: 1,
          }}
          showTile={true}
        />
        <Markdown>{totalOwnedText}</Markdown>
        <View style={styles.costContainer}>
          <Markdown>Cost:</Markdown>
          <CostViewer resources={cost.resources} items={cost.items} />
        </View>
        <View style={styles.actionRow}>
          {isCraftable ? (
            [
              <Button
                key="craft_button"
                enableAutoRepress={true}
                autoRepressConfig={{
                  dropoffMultiplier: 0.8,
                  startingThreshold: 300,
                  startingRepressTime: 200,
                  minTimeMs: 50,
                }}
                title="Craft"
                disabled={!canCraft}
                onPress={craftCallback}
              />,
              !canCraft && hasCraftingMaterials && inventoryFull && (
                <View
                  style={styles.inventoryFullContainer}
                  key="inventory_full"
                >
                  <Text style={{ color: theme.theme.colors.error }}>
                    You cannot craft this item as your inventory is full!
                  </Text>
                </View>
              ),
            ]
          ) : (
            <Text
              style={[
                styles.cannotCraftText,
                { color: theme.theme.colors.warning },
              ]}
            >
              {cannotCraftText}
            </Text>
          )}
        </View>
      </View>
    ),
    [
      item.getId(),
      JSON.stringify(params),
      totalOwnedText,
      JSON.stringify(cost.resources),
      JSON.stringify(cost.items),
      canCraft,
      inventoryFull,
      playerContextRef,
      theme,
      isCraftable,
    ],
  );
}

const styles = StyleSheet.create({
  craftingDetailsContainer: {
    marginBottom: 16,
  },
  tagRow: {
    marginVertical: 4,
  },
  actionRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 8,
  },
  actionButtonContainer: {
    marginRight: 8,
  },
  costContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginTop: 4,
  },
  costTextContainer: {
    marginRight: 8,
  },
  inventoryFullContainer: {
    flex: 1,
    marginHorizontal: 8,
  },
  cannotCraftText: {
    fontStyle: "italic",
  },
});
